import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { CreateJobCardRequest } from '../../types/jobcard';
import { useForm } from 'react-hook-form';
import { useStores } from '../../hooks/useStores';
import { showErrorToast } from '../../utils/toast';
import { useNavigate } from 'react-router-dom';
import { Store } from '../../types/store';
import { checkExistingJobCard, updateJobCard } from '../../services/jobcardApi';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (data: CreateJobCardRequest) => Promise<{ data: { id: number } }>;
};

type FormData = {
    store_id: string;
    date: string;
    time_in: string;
    time_out: string;
    travel_distance: number;
    order_number?: string;
    user_id?: number;
};

export default function CreateJobCardModal({ isOpen, onClose, onSuccess }: Props) {
    const { stores } = useStores();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [timeOverlapError, setTimeOverlapError] = useState<string | null>(null);
    const [orderNumberError, setOrderNumberError] = useState<string | null>(null);
    const [orderNumberWarning, setOrderNumberWarning] = useState<string | null>(null);
    const [showExistingJobCardModal, setShowExistingJobCardModal] = useState(false);
    const [existingJobCard, setExistingJobCard] = useState<any>(null);
    const [formData, setFormData] = useState<FormData | null>(null);

    const { register, handleSubmit, reset, formState: { errors }, watch } = useForm<FormData>({
        defaultValues: {
            store_id: '',
            date: new Date().toISOString().split('T')[0],
            time_in: new Date().toTimeString().slice(0, 5),
            time_out: new Date(Date.now() + 60 * 60 * 1000).toTimeString().slice(0, 5),
            travel_distance: 0,
            order_number: ''
        }
    });

    const orderNumber = watch('order_number');

    const handleOrderNumberChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setOrderNumberError(null); // Clear previous errors
        setOrderNumberWarning(null); // Clear previous warnings
        
        if (value && !value.includes(',') && !value.includes(';')) {
            try {
                const response = await checkExistingJobCard(value);
                if (response.data.exists) {
                    setExistingJobCard(response.data.jobCard);
                } else if (response.data.valid === false) {
                    // Order number is invalid (doesn't exist in PO system)
                    setOrderNumberError(response.data.message || 'Invalid order number');
                } else if (response.data.fallback_behavior === 'set_null') {
                    // Order number validation is unavailable - will be set to null
                    setOrderNumberWarning(response.data.message || 'Order number validation is unavailable. The order number will be set to null.');
                }
            } catch (error: any) {
                console.error('Error checking existing job card:', error);
                // Handle validation errors from the backend
                if (error.response?.status === 422 && error.response?.data?.error) {
                    setOrderNumberError(error.response.data.error);
                } else if (error.response?.status === 503) {
                    // Database unavailable - show warning but allow proceeding
                    setOrderNumberWarning('Order number validation is temporarily unavailable. The order number will be set to null.');
                } else {
                    // Other errors - show as warnings to allow graceful degradation
                    setOrderNumberWarning('Unable to validate order number at this time. The order number will be set to null.');
                }
            }
        }
    };

    const handleProceedToExisting = async () => {
        try {
            console.log('handleProceedToExisting called');
            console.log('formData:', formData);
            console.log('existingJobCard:', existingJobCard);
            if (formData) {
                await updateJobCard(existingJobCard.id, {
                    store_id: parseInt(formData.store_id),
                    date: formData.date,
                    time_in: formData.time_in,
                    time_out: formData.time_out,
                    travel_distance: Number(formData.travel_distance),
                    order_number: formData.order_number
                });
            } else {
                console.warn('formData is null, updateJobCard not called');
            }
            setShowExistingJobCardModal(false);
            onClose();
            navigate(`/jobcards/edit/${existingJobCard.id}`);
        } catch (error) {
            console.error('Error updating existing job card:', error);
            showErrorToast('Failed to update existing job card');
        }
    };

    const handleContinueCreating = () => {
        setShowExistingJobCardModal(false);
    };

    const onSubmit = async (formData: FormData) => {
        try {
            setIsSubmitting(true);
            setTimeOverlapError(null);
            setOrderNumberError(null);
            setOrderNumberWarning(null);

            if (formData.order_number && !formData.order_number.includes(',') && !formData.order_number.includes(';')) {
                try {
                    const response = await checkExistingJobCard(formData.order_number);
                    if (response.data.exists) {
                        setExistingJobCard(response.data.jobCard);
                        setFormData(formData);
                        setShowExistingJobCardModal(true);
                        setIsSubmitting(false);
                        return;
                    } else if (response.data.valid === false) {
                        // Order number is invalid
                        setOrderNumberError(response.data.message || 'Invalid order number');
                        setIsSubmitting(false);
                        return;
                    } else if (response.data.fallback_behavior === 'set_null') {
                        // Order number validation is unavailable - will be set to null, but allow proceeding
                        setOrderNumberWarning(response.data.message || 'Order number validation is unavailable. The order number will be set to null.');
                        // Continue with submission - don't return here
                    }
                } catch (error: any) {
                    console.error('Error checking existing job card during submit:', error);
                    // Handle validation errors from the backend
                    if (error.response?.status === 422 && error.response?.data?.error) {
                        setOrderNumberError(error.response.data.error);
                        setIsSubmitting(false);
                        return;
                    } else if (error.response?.status === 503) {
                        setOrderNumberWarning('Order number validation is temporarily unavailable. The order number will be set to null.');
                        setIsSubmitting(false);
                        return;
                    } else {
                        // Other errors - show as warnings to allow graceful degradation
                        setOrderNumberWarning('Unable to validate order number at this time. The order number will be set to null.');
                        setIsSubmitting(false);
                        return;
                    }
                }
            }

            const dataToSubmit: CreateJobCardRequest = {
                ...formData,
                store_id: parseInt(formData.store_id),
                travel_distance: Number(formData.travel_distance)
            };

            const response = await onSuccess(dataToSubmit);
            if (response?.data?.id) {
                reset();
                onClose();
                setTimeout(() => {
                    navigate(`/jobcards/edit/${response.data.id}`);
                }, 100);
            }
        } catch (error: any) {
            if (error.response?.data?.message === 'Time overlaps with another job card') {
                const overlappingCard = error.response.data.overlapping_jobcard;
                const formatTime = (time: string) => {
                    const [hours, minutes] = time.split(':');
                    return `${hours}:${minutes}`;
                };
                setTimeOverlapError(
                    `Times overlap with job card ${overlappingCard.job_number} ` +
                    `(${formatTime(overlappingCard.time_in)} - ${formatTime(overlappingCard.time_out)})`
                );
            } else if (error.response?.data?.errors?.order_number) {
                // Handle order number validation errors from job card creation
                setOrderNumberError(error.response.data.errors.order_number[0]);
            } else {
                showErrorToast(error.response?.data?.message || 'Failed to create job card');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Dialog open={isOpen} onClose={onClose} className="relative z-50">
                <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                        <div className="flex justify-between items-center mb-4">
                            <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                                Create New Job Card
                            </Dialog.Title>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Store
                                </label>
                                <select
                                    {...register('store_id', { required: 'Store is required' })}
                                    className="w-full rounded-md px-2 border border-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                >
                                    <option value="">Select a store</option>
                                    {stores.map((store: Store) => (
                                        <option key={store.id} value={store.id}>
                                            {store.name} (#{store.storenumber})
                                        </option>
                                    ))}
                                </select>
                                {errors.store_id && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {errors.store_id.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    {...register('date', { required: 'Date is required' })}
                                    className="w-full rounded-md px-2 border border-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                />
                                {errors.date && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {errors.date.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Time In
                                </label>
                                <input
                                    type="time"
                                    {...register('time_in', { required: 'Time in is required' })}
                                    className="w-full rounded-md px-2 border border-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                />
                                {errors.time_in && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {errors.time_in.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Time Out
                                </label>
                                <input
                                    type="time"
                                    {...register('time_out', { required: 'Time out is required' })}
                                    className="w-full rounded-md px-2 border border-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                />
                                {errors.time_out && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {errors.time_out.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Travel Distance (km)
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    {...register('travel_distance', {
                                        required: 'Travel distance is required',
                                        min: { value: 0, message: 'Travel distance must be positive' }
                                    })}
                                    className="w-full rounded-md px-2 border border-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                />
                                {errors.travel_distance && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {errors.travel_distance.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Order Number (optional)
                                </label>
                                <input
                                    type="text"
                                    {...register('order_number')}
                                    onChange={(e) => {
                                        register('order_number').onChange(e);
                                        handleOrderNumberChange(e);
                                    }}
                                    className="w-full rounded-md px-2 border border-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                />
                                <small className="text-gray-500 dark:text-gray-400">
                                    If the order number is not an internal order, leave blank.
                                </small>
                                {orderNumberError && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {orderNumberError}
                                    </p>
                                )}
                                {orderNumberWarning && (
                                    <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
                                        {orderNumberWarning}
                                    </p>
                                )}
                            </div>

                            {timeOverlapError && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
                                    <p className="text-sm text-red-600 dark:text-red-300">
                                        {timeOverlapError}
                                    </p>
                                </div>
                            )}

                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !!orderNumberError}
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Creating...' : 'Create Job Card'}
                                </button>
                            </div>
                        </form>
                    </Dialog.Panel>
                </div>
            </Dialog>

            {/* Existing Job Card Modal */}
            <Dialog open={showExistingJobCardModal} onClose={() => setShowExistingJobCardModal(false)} className="relative z-50">
                <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            Existing Job Card Found
                        </Dialog.Title>

                        <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                A job card already exists for order number {orderNumber}. Would you like to:
                            </p>
                            <ul className="mt-4 space-y-2">
                                <li className="text-sm text-gray-700 dark:text-gray-300">
                                    • Proceed to the existing job card and update it with your entered information
                                </li>
                                <li className="text-sm text-gray-700 dark:text-gray-300">
                                    • Continue creating a new job card
                                </li>
                            </ul>
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleContinueCreating}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                            >
                                Continue Creating
                            </button>
                            <button
                                type="button"
                                onClick={handleProceedToExisting}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600"
                            >
                                Proceed to Existing
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </>
    );
} 